import {
  BuildingIcon,
  CircleCheckIcon,
  ExternalLinkIcon,
  FactoryIcon,
  GlobeIcon,
  MapPinIcon,
  PhoneIcon,
  UsersIcon,
} from "lucide-react";

import { countries, regions } from "simplydo/core/countries";
import {
  accreditations,
  businessSizeOptions,
  businessStatusOptions,
  businessTypeOptions,
} from "simplydo/core/ideaBusinessProfiles";
import { ExternalLinkModal } from "simplydo/react/components/ExternalLinkModal";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Label } from "simplydo/react/ui/label";

import CompaniesHouseIcon from "src/images/companiesHouse-icon.png";

import { TagView } from "../../Choosers/Tags/TagModalChooser";
import type { CompaniesHouseCompany, IdeaBusinessProfile } from "../types";

type OverviewProps = { businessProfile: IdeaBusinessProfile };

export const Overview = ({ businessProfile }: OverviewProps) => {
  const { address, ownerTags, foundingYear } = businessProfile || {};

  const { country, city, streetAddress, postcode, region } = address || {};
  const size = businessSizeOptions.find(
    (option) => option.value === businessProfile?.size,
  )?.text;
  const type = businessTypeOptions.find(
    (option) => option.value === businessProfile?.type,
  )?.text;
  const status = businessStatusOptions.find(
    (option) => option.value === businessProfile?.status,
  )?.text;
  const fullAddress = [
    streetAddress,
    city,
    countries.find((c) => c.iso2 === country)?.name,
    regions.find((r) => r.value === region)?.name,
    postcode,
  ].filter((addressSegment) => !!addressSegment);

  const companiesHouseCompany = businessProfile.companiesHouse?.company as
    | CompaniesHouseCompany
    | undefined;

  return (
    <>
      <div style={{ display: "flex", gap: 24 }}>
        {businessProfile?.imageUrl && (
          <div
            style={{
              display: "flex",
              alignSelf: "flex-start",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 4,
              overflow: "hidden",
              height: 150,
              width: 150,
            }}
          >
            <Image
              src={businessProfile?.imageUrl}
              alt={businessProfile?.name}
            />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <H4>About</H4>
          {businessProfile?.websiteUrl && (
            <p style={{ marginBottom: 7 }}>
              <Icon icon={GlobeIcon} />
              <ExternalLinkModal href={businessProfile.websiteUrl}>
                {businessProfile.websiteUrl}
              </ExternalLinkModal>
            </p>
          )}

          {fullAddress && fullAddress.length > 0 ? (
            <p style={{ marginBottom: 7 }}>
              <Icon icon={MapPinIcon} />
              {fullAddress.join(", ")}
            </p>
          ) : null}

          {businessProfile?.phoneNumber && (
            <p style={{ marginBottom: 7 }}>
              <Icon icon={PhoneIcon} />
              {businessProfile?.phoneNumber}
            </p>
          )}

          {size && (
            <p style={{ marginBottom: 7 }}>
              <Icon icon={UsersIcon} />
              {size}
            </p>
          )}

          {(status || foundingYear) && (
            <p style={{ marginBottom: 7 }}>
              <Icon icon={BuildingIcon} />
              {foundingYear ? `Founded: ${foundingYear}` : ""}
              {status ? `${foundingYear ? " | " : ""}${status}` : ""}
            </p>
          )}

          {type && (
            <p style={{ marginBottom: 7 }}>
              <Icon icon={FactoryIcon} />
              {type}
            </p>
          )}
        </div>
      </div>
      <Divider hidden />
      {!businessProfile?.creditsafeRegNoSkipped &&
        businessProfile?.creditsafeRegNo && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 5,
              fontWeight: "bold",
            }}
          >
            <p style={{ marginBottom: 0, marginRight: 5 }}>
              Organisation registration number
            </p>
            <Label size="small">{businessProfile?.creditsafeRegNo}</Label>
          </div>
        )}
      {!businessProfile?.vatNoSkipped && businessProfile?.vatNo && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 5,
            fontWeight: "bold",
          }}
        >
          <p style={{ marginBottom: 0, marginRight: 5 }}>VAT number</p>
          <Label size="small">{businessProfile?.vatNo}</Label>
        </div>
      )}
      {businessProfile?.accreditations &&
        businessProfile?.accreditations?.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 5,
              fontWeight: "bold",
            }}
          >
            <p style={{ marginBottom: 0, marginRight: 5 }}>Accreditations</p>
            {businessProfile?.accreditations.map((accreditation) => (
              <Label key={accreditation} size="small">
                {accreditations.find((acc) => acc?.key === accreditation)
                  ?.text || accreditation}
              </Label>
            ))}
          </div>
        )}
      {businessProfile?.tags && businessProfile?.tags?.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 5,
            fontWeight: "bold",
            marginTop: "-8px",
          }}
        >
          <p style={{ marginBottom: 0, marginRight: 10 }}>Tags</p>
          <TagView tags={ownerTags || []} />
        </div>
      )}
      <Divider hidden />
      <p>{businessProfile?.description}</p>
      {businessProfile?.companiesHouse?.isAvailable ? (
        <Card fluid placeholder>
          <CardContent>
            <b>Companies House check</b>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 5,
                gap: 8,
              }}
            >
              <Image
                src={CompaniesHouseIcon}
                style={{ width: 50, height: 50, objectFit: "contain" }}
              />
              <Icon icon={CircleCheckIcon} color="blue" size="large" />
              <div>
                The company number &quot;
                {businessProfile?.creditsafeRegNo}
                &quot; is registered as &quot;
                {companiesHouseCompany?.company_name}
                &quot;
                <br />
                {companiesHouseCompany?.links?.self ? (
                  <a
                    href={`https://beta.companieshouse.gov.uk${companiesHouseCompany.links.self}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View company profile on Companies House
                    <Icon
                      icon={ExternalLinkIcon}
                      size="small"
                      style={{
                        lineHeight: 1,
                        verticalAlign: "text-top",
                        marginTop: 5,
                        marginLeft: 2,
                      }}
                    />
                  </a>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </>
  );
};
